/**
 * CoursCanevasPresentation.jsx — Mode Pro Studio générique
 *
 * Routes :
 *   - /enseignant/terrain/:slug/presenter           (fiches plateau)
 *   - /enseignant/seance-circulation/:slug/presenter (fiches circulation REMC)
 *
 * Prend une fiche conforme au canevas REMC 9 phases (objectif, evalStatique,
 * evalDynamique, contrat, cours, demonstration, guidage, autonomie, bilan)
 * et la transforme en présentation slide-par-slide animée (framer-motion).
 *
 * Couche PREMIUM optionnelle (via /data/teacher/fichesInteractives.js) :
 *   - injection de slides QCM / Vidéo / Stat-choc / Schéma SVG / PhraseChoc
 *   - sons de feedback sur QCM
 *
 * Réutilise le hook useSoundEffects pour les feedbacks audio.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronLeft, ChevronRight, Volume2, VolumeX, Sparkles,
  ClipboardCheck, Activity, Target, ListChecks, BookOpen, Eye,
  Hand, Award, CheckCircle2, XCircle, Quote, Flame, Lightbulb,
  BarChart3, Video,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import useSoundEffects from '../hooks/useSoundEffects';
import { getFichePlateauBySlug } from '../data/teacher/fichesPlateau';
import { getFicheCirculationBySlug } from '../data/teacher/fichesCirculation';
import { getInteractifBySlug } from '../data/teacher/fichesInteractives';

// Mapping nom de phase canevas → icône + couleur
const PHASE_META = {
  evalStatique:  { label: 'Bilan — Évaluation statique', color: '#0891b2', icon: ClipboardCheck, intent: "L'élève parle, le moniteur écoute" },
  evalDynamique: { label: 'Bilan — Évaluation dynamique', color: '#0891b2', icon: Activity, intent: 'Constat factuel du point de départ' },
  objectif:      { label: 'Contrat — Objectif de la séance', color: '#cf2031', icon: Target, intent: 'Phrase observable et mesurable' },
  criteres:      { label: 'Contrat — Critères de réussite', color: '#cf2031', icon: ListChecks, intent: 'Fil rouge de la séance' },
  cours:         { label: 'Apprentissage — Cours (apports)', color: '#16a34a', icon: BookOpen, intent: 'Schéma + explication courte' },
  demonstration: { label: 'Apprentissage — Démonstration', color: '#16a34a', icon: Eye, intent: 'SCHÉMA → DÉMO → ESSAIS' },
  guidage:       { label: 'Apprentissage — Guidage dégressif', color: '#16a34a', icon: Hand, intent: 'FORT → MOYEN → FAIBLE → gestuel' },
  autonomie:     { label: 'Apprentissage — Autonomie', color: '#16a34a', icon: Sparkles, intent: '3-5 passages sans intervention' },
  bilan:         { label: 'Bilan final', color: '#9333ea', icon: CheckCircle2, intent: "Tu parles d'abord · 2 + 1" },
};

const PHASE_ORDER = ['evalStatique','evalDynamique','objectif','criteres','cours','demonstration','guidage','autonomie','bilan'];

/* ─── Construction des slides ────────────────────────────────────── */
function buildSlides(fiche, kind, interactif) {
  const slides = [];
  const accent = kind === 'plateau' ? '#0891b2' : '#cf2031';
  const steps = fiche.steps || fiche;
  const enrichissements = interactif?.enrichissements || [];

  // Helper : récupère les enrichissements à injecter après une phase donnée
  const enrichmentsFor = (phaseKey) =>
    enrichissements.filter((e) => e.injectAfter === phaseKey);

  // 0 — Titre
  slides.push({
    kind: 'title',
    accent,
    title: fiche.title || fiche.titre,
    subtitle: steps?.objectif?.content || fiche.kicker || fiche.intent || '',
    duree: fiche.duree || fiche.meta?.duree || fiche.dureeTotal,
    category: kind === 'plateau' ? 'Fiche Plateau' : 'Fiche Circulation REMC',
    premium: Boolean(interactif),
  });

  // Enrichissements après "title"
  enrichmentsFor('title').forEach((e) => slides.push({ ...e.slide, accent: e.slide.accent || accent }));

  // Phrase-clé en intro si présente
  if (fiche.phraseClef || fiche.phraseCle) {
    slides.push({
      kind: 'phraseCle',
      accent: '#d97706',
      texte: fiche.phraseClef || fiche.phraseCle,
    });
    enrichmentsFor('phraseCle').forEach((e) => slides.push({ ...e.slide, accent: e.slide.accent || accent }));
  }

  // Phases canevas — depuis steps[key]
  PHASE_ORDER.forEach((key) => {
    const phase = steps?.[key];
    if (!phase) return;
    const meta = PHASE_META[key];
    slides.push({
      kind: 'phase',
      accent: meta.color,
      icon: meta.icon,
      label: meta.label,
      intent: meta.intent,
      content: phase.content || phase.titre || '',
      items: phase.items || [],
      duration: phase.duree || phase.duration || null,
    });
    // Injection des enrichissements après cette phase
    enrichmentsFor(key).forEach((e) => slides.push({ ...e.slide, accent: e.slide.accent || meta.color }));
  });

  // Enrichissements ciblant "fin" (avant la slide finale)
  enrichmentsFor('fin').forEach((e) => slides.push({ ...e.slide, accent: e.slide.accent || accent }));

  // Fin
  slides.push({
    kind: 'end',
    accent: '#16a34a',
    title: 'Bilan de séance',
    subtitle: "Auto-évaluation D'ABORD, puis 2 réussites + 1 axe par le moniteur.",
  });

  return slides;
}

/* ─── Shell commun ───────────────────────────────────────────────── */
function Shell({ children, accent, badge, icon: Icon }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.35 }} className="w-full h-full flex flex-col"
    >
      {badge && (
        <div className="flex items-center gap-3 mb-6 px-4 py-2 rounded-2xl backdrop-blur w-fit"
          style={{ background: `${accent}22`, border: `1px solid ${accent}66` }}>
          {Icon && <Icon className="w-5 h-5" style={{ color: accent }} />}
          <span className="text-sm font-black uppercase tracking-[0.2em] text-white/90">{badge}</span>
        </div>
      )}
      <div className="flex-1 flex flex-col justify-center">{children}</div>
    </motion.div>
  );
}

/* ─── Renderers de base ──────────────────────────────────────────── */
function TitleSlide({ slide }) {
  return (
    <Shell accent={slide.accent} badge={slide.category} icon={Flame}>
      <div className="text-center">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="inline-flex w-20 h-20 rounded-3xl items-center justify-center mb-6"
          style={{ background: slide.accent }}>
          <Flame className="w-10 h-10 text-white" strokeWidth={2.2} />
        </motion.div>
        <h1 className="text-5xl md:text-7xl font-black leading-tight text-white"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{slide.title}</h1>
        {slide.subtitle && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="mt-4 text-xl md:text-2xl text-white/70 font-bold max-w-4xl mx-auto leading-snug"
            dangerouslySetInnerHTML={{ __html: slide.subtitle }} />
        )}
        {slide.duree && (
          <div className="mt-6 inline-block px-4 py-2 rounded-full text-sm font-black uppercase tracking-widest text-white"
            style={{ background: `${slide.accent}33`, border: `2px solid ${slide.accent}` }}>
            ⏱ {slide.duree}
          </div>
        )}
        {slide.premium && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest ml-2"
            style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white' }}
          >
            <Sparkles className="w-3.5 h-3.5" /> Mode Premium
          </motion.div>
        )}
      </div>
    </Shell>
  );
}

function PhraseCleSlide({ slide }) {
  return (
    <Shell accent={slide.accent} badge="Phrase clef à retenir" icon={Quote}>
      <div className="max-w-5xl mx-auto text-center">
        <Quote className="w-16 h-16 mx-auto mb-4" style={{ color: slide.accent }} />
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="text-3xl md:text-5xl font-black leading-tight text-white italic"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          « {slide.texte} »
        </motion.p>
      </div>
    </Shell>
  );
}

function PhaseSlide({ slide }) {
  return (
    <Shell accent={slide.accent} badge={slide.label} icon={slide.icon}>
      <div className="max-w-5xl mx-auto">
        {slide.intent && (
          <p className="text-base md:text-lg italic text-white/60 mb-4">{slide.intent}</p>
        )}
        {slide.content && (
          <motion.p initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
            className="text-2xl md:text-4xl font-black leading-tight text-white mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            dangerouslySetInnerHTML={{ __html: slide.content }} />
        )}
        {slide.items && slide.items.length > 0 && (
          <ul className="space-y-3">
            {slide.items.map((it, i) => (
              <motion.li key={i}
                initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.35 }}
                className="flex items-start gap-3 p-3 rounded-2xl"
                style={{ background: `${slide.accent}15`, border: `1px solid ${slide.accent}44` }}>
                <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-black text-sm shrink-0 mt-0.5"
                  style={{ background: slide.accent }}>{i + 1}</span>
                <span className="text-lg md:text-xl font-bold text-white leading-snug" dangerouslySetInnerHTML={{ __html: it }} />
              </motion.li>
            ))}
          </ul>
        )}
        {slide.duration && (
          <div className="mt-6 inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest text-white/70 border border-white/20">
            ⏱ {slide.duration}
          </div>
        )}
      </div>
    </Shell>
  );
}

function EndSlide({ slide }) {
  return (
    <Shell accent={slide.accent} badge="Fin de séance" icon={Award}>
      <div className="text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 120 }}
          className="inline-flex w-24 h-24 rounded-3xl items-center justify-center mb-6"
          style={{ background: slide.accent }}>
          <Award className="w-12 h-12 text-white" strokeWidth={2.2} />
        </motion.div>
        <h1 className="text-5xl md:text-7xl font-black text-white leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{slide.title}</h1>
        <p className="mt-4 text-xl md:text-3xl text-white/80 font-bold max-w-4xl mx-auto">{slide.subtitle}</p>
      </div>
    </Shell>
  );
}

/* ─── Renderers PREMIUM ──────────────────────────────────────────── */

function QcmSlide({ slide, onReveal }) {
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const correctIdx = slide.options.findIndex((o) => o.correct);

  // Note : pas de useEffect de reset — le composant se remonte
  // à chaque changement de slide grâce à `key={idx}` sur le wrapper parent.

  const handleReveal = () => {
    setRevealed(true);
    if (onReveal) onReveal(picked === correctIdx);
  };

  return (
    <Shell accent={slide.accent || '#7c3aed'} badge="🎯 QCM — Lève la main" icon={Lightbulb}>
      <div className="max-w-5xl mx-auto w-full">
        {slide.consigne && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="mb-4 inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest text-white/70 border border-white/20"
          >
            ✋ {slide.consigne}
          </motion.div>
        )}
        <motion.h2
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-5xl font-black leading-tight text-white mb-8"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {slide.question}
        </motion.h2>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="qcm-options">
          {slide.options.map((opt, i) => {
            const isPicked = i === picked;
            const isCorr = i === correctIdx;
            let bg = 'rgba(255,255,255,0.05)';
            let border = 'rgba(255,255,255,0.2)';
            if (revealed) {
              if (isCorr) { bg = 'rgba(22,163,74,0.25)'; border = '#16a34a'; }
              else if (isPicked) { bg = 'rgba(220,38,38,0.25)'; border = '#dc2626'; }
            } else if (isPicked) {
              bg = `${slide.accent}33`; border = slide.accent;
            }
            return (
              <motion.button
                key={`opt-${i}`}
                whileHover={!revealed ? { scale: 1.02 } : {}}
                whileTap={!revealed ? { scale: 0.98 } : {}}
                onClick={() => !revealed && setPicked(i)}
                disabled={revealed}
                data-testid={`qcm-opt-${i}`}
                className="text-left p-4 rounded-2xl border-2 transition-all"
                style={{ background: bg, borderColor: border }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-black shrink-0"
                    style={{ background: revealed && isCorr ? '#16a34a' : revealed && isPicked ? '#dc2626' : (isPicked ? slide.accent : 'rgba(255,255,255,0.1)') }}
                  >
                    {revealed && isCorr ? <CheckCircle2 className="w-5 h-5" /> : (revealed && isPicked && !isCorr ? <XCircle className="w-5 h-5" /> : String.fromCharCode(65 + i))}
                  </div>
                  <span className="text-lg md:text-xl font-bold text-white leading-snug pt-1.5">{opt.label}</span>
                </div>
              </motion.button>
            );
          })}
        </div>
        <AnimatePresence>
          {revealed && slide.explication && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-6 p-4 rounded-2xl border-2 border-amber-400 bg-amber-500/10 flex items-start gap-3"
              data-testid="qcm-explication"
            >
              <Lightbulb className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-base md:text-lg text-white leading-relaxed">{slide.explication}</p>
            </motion.div>
          )}
        </AnimatePresence>
        {!revealed && (
          <div className="mt-6 flex justify-center">
            <Button
              onClick={handleReveal}
              disabled={picked === null}
              className="gap-2 bg-white text-black hover:bg-white/90 font-black uppercase tracking-wider px-6 py-5 text-base"
              data-testid="qcm-reveal"
            >
              <Sparkles className="w-5 h-5" /> Révéler la réponse
            </Button>
          </div>
        )}
      </div>
    </Shell>
  );
}

function VideoSlide({ slide }) {
  const url = slide.videoUrl || '';
  let embedSrc = url;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (match) {
    embedSrc = `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1`;
  }
  return (
    <Shell accent={slide.accent || '#dc2626'} badge="🎥 Vidéo à projeter" icon={Video}>
      <div className="max-w-5xl mx-auto w-full">
        <div className="aspect-video w-full rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl bg-black">
          {url ? (
            <iframe
              src={embedSrc}
              title="Vidéo pédagogique"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="video-iframe"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/40 italic text-lg">
              📺 URL YouTube à renseigner dans la couche interactive
            </div>
          )}
        </div>
        {slide.caption && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="mt-4 text-center text-base md:text-lg text-white/70 italic">
            {slide.caption}
          </motion.p>
        )}
      </div>
    </Shell>
  );
}

function StatSlide({ slide }) {
  return (
    <Shell accent={slide.accent || '#dc2626'} badge="📊 Chiffre-choc" icon={BarChart3}>
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 120, damping: 12 }}
          className="text-7xl md:text-[10rem] font-black leading-none mb-4"
          style={{
            color: slide.accent || '#dc2626',
            textShadow: `0 0 60px ${slide.accent || '#dc2626'}66`,
            fontFamily: "'Source Code Pro', monospace",
          }}
        >
          {slide.valeur}
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-2xl md:text-3xl font-black text-white uppercase tracking-wider"
        >
          {slide.label}
        </motion.p>
        {slide.detail && (
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="mt-4 text-base md:text-lg text-white/60 max-w-3xl mx-auto"
          >
            {slide.detail}
          </motion.p>
        )}
      </div>
    </Shell>
  );
}

function PhraseChocSlide({ slide }) {
  return (
    <Shell accent={slide.accent || '#f59e0b'} badge="💥 À retenir" icon={Quote}>
      <div className="max-w-5xl mx-auto text-center">
        <Quote className="w-20 h-20 mx-auto mb-6" style={{ color: slide.accent || '#f59e0b' }} />
        <motion.p
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-4xl md:text-6xl font-black leading-tight text-white italic"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          « {slide.texte} »
        </motion.p>
        {slide.auteur && (
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="mt-8 text-base md:text-lg text-white/60 uppercase tracking-widest font-bold"
          >
            — {slide.auteur}
          </motion.p>
        )}
      </div>
    </Shell>
  );
}

/* ─── Schémas SVG animés ─────────────────────────────────────────── */

function SchemaFreinage7030() {
  // Couleurs fixes pour rendre lisible le contraste 70/30
  const avantColor = '#16a34a';
  const arriereColor = '#dc2626';
  return (
    <svg viewBox="0 0 600 280" className="w-full max-w-3xl mx-auto">
      {/* AVANT - 70% */}
      <text x="20" y="60" fill="white" fontSize="22" fontWeight="900" fontFamily="Playfair Display">AVANT</text>
      <rect x="20" y="75" width="540" height="38" rx="6" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.2)" />
      <motion.rect
        x="20" y="75" height="38" rx="6"
        fill={avantColor}
        initial={{ width: 0 }}
        animate={{ width: 540 * 0.7 }}
        transition={{ duration: 1.2, delay: 0.3, ease: 'easeOut' }}
      />
      <motion.text
        x={20 + 540 * 0.7 + 14} y="100"
        fill={avantColor} fontSize="28" fontWeight="900" fontFamily="Source Code Pro"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
      >70 %</motion.text>

      {/* ARRIÈRE - 30% */}
      <text x="20" y="170" fill="white" fontSize="22" fontWeight="900" fontFamily="Playfair Display">ARRIÈRE</text>
      <rect x="20" y="185" width="540" height="38" rx="6" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.2)" />
      <motion.rect
        x="20" y="185" height="38" rx="6"
        fill={arriereColor}
        initial={{ width: 0 }}
        animate={{ width: 540 * 0.3 }}
        transition={{ duration: 1.0, delay: 0.6, ease: 'easeOut' }}
      />
      <motion.text
        x={20 + 540 * 0.3 + 14} y="210"
        fill={arriereColor} fontSize="28" fontWeight="900" fontFamily="Source Code Pro"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
      >30 %</motion.text>

      <motion.text
        x="300" y="265" textAnchor="middle"
        fill="white" fontSize="14" fontWeight="700" opacity="0.7"
        initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 2 }}
      >
        Le transfert de masse vers l&apos;avant rend la roue avant plus efficace.
      </motion.text>
    </svg>
  );
}

function SchemaSlalomS({ accent }) {
  // 5 cônes en ligne + trajectoire en S
  const cones = [120, 220, 320, 420, 520];
  return (
    <svg viewBox="0 0 640 280" className="w-full max-w-3xl mx-auto">
      {/* Route */}
      <rect x="40" y="120" width="560" height="80" rx="4" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.15)" strokeDasharray="6 4" />

      {/* Cônes */}
      {cones.map((cx, i) => (
        <motion.g key={i}
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 + i * 0.1 }}
        >
          <polygon points={`${cx - 12},170 ${cx + 12},170 ${cx},135`} fill="#ea580c" />
          <rect x={cx - 14} y="170" width="28" height="6" fill="#ea580c" />
          <text x={cx} y="195" textAnchor="middle" fill="white" fontSize="14" fontWeight="900">{i + 1}</text>
        </motion.g>
      ))}

      {/* Trajectoire en S */}
      <motion.path
        d="M 60 160 Q 120 110 170 160 Q 220 210 270 160 Q 320 110 370 160 Q 420 210 470 160 Q 520 110 580 160"
        fill="none"
        stroke={accent || '#7c3aed'}
        strokeWidth="5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2.4, delay: 0.8, ease: 'easeInOut' }}
      />

      {/* Regard porté 2 cônes à l'avance */}
      <motion.g
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.8 }}
      >
        <circle cx="220" cy="70" r="14" fill={accent || '#7c3aed'} />
        <text x="220" y="75" textAnchor="middle" fill="white" fontSize="14" fontWeight="900">👁</text>
        <text x="220" y="40" textAnchor="middle" fill="white" fontSize="13" fontWeight="900">REGARD</text>
        <motion.line
          x1="220" y1="84" x2="420" y2="155"
          stroke={accent || '#7c3aed'} strokeWidth="2" strokeDasharray="4 4"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 3.0, duration: 0.6 }}
        />
        <text x="420" y="120" textAnchor="middle" fill="white" fontSize="12" opacity="0.7">+2 cônes</text>
      </motion.g>
    </svg>
  );
}

function SchemaRegle2Sec({ accent }) {
  return (
    <svg viewBox="0 0 640 280" className="w-full max-w-3xl mx-auto">
      {/* Route */}
      <rect x="20" y="160" width="600" height="60" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.15)" />
      <line x1="20" y1="190" x2="620" y2="190" stroke="rgba(255,255,255,0.3)" strokeDasharray="14 10" />

      {/* Véhicule de devant */}
      <motion.g initial={{ x: -100 }} animate={{ x: 0 }} transition={{ duration: 0.8 }}>
        <rect x="480" y="170" width="60" height="30" rx="6" fill="#9ca3af" />
        <rect x="490" y="175" width="40" height="14" rx="2" fill="#475569" />
        <circle cx="495" cy="200" r="6" fill="#1f2937" />
        <circle cx="525" cy="200" r="6" fill="#1f2937" />
      </motion.g>

      {/* Ta moto */}
      <motion.g initial={{ x: -100 }} animate={{ x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
        <rect x="100" y="170" width="40" height="30" rx="4" fill={accent || '#cf2031'} />
        <circle cx="108" cy="200" r="7" fill="#1f2937" />
        <circle cx="132" cy="200" r="7" fill="#1f2937" />
        <text x="120" y="158" textAnchor="middle" fill="white" fontSize="11" fontWeight="900">TOI</text>
      </motion.g>

      {/* Flèche distance */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }}>
        <line x1="150" y1="140" x2="470" y2="140" stroke="white" strokeWidth="2" markerEnd="url(#arr)" markerStart="url(#arr2)" />
        <defs>
          <marker id="arr" markerWidth="10" markerHeight="10" refX="6" refY="3" orient="auto">
            <path d="M 0 0 L 6 3 L 0 6 Z" fill="white" />
          </marker>
          <marker id="arr2" markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto">
            <path d="M 6 0 L 0 3 L 6 6 Z" fill="white" />
          </marker>
        </defs>
        <rect x="270" y="110" width="80" height="36" rx="6" fill="black" stroke={accent || '#cf2031'} strokeWidth="2" />
        <text x="310" y="135" textAnchor="middle" fill={accent || '#cf2031'} fontSize="22" fontWeight="900" fontFamily="Source Code Pro">2s</text>
      </motion.g>

      {/* Compteur 21-22 */}
      <motion.text
        x="320" y="255" textAnchor="middle"
        fill="white" fontSize="20" fontWeight="900"
        initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 1, 1] }}
        transition={{ delay: 1.6, duration: 2.4, times: [0, 0.1, 0.5, 1] }}
      >
        « 21 — 22 »
      </motion.text>
      <motion.text
        x="320" y="275" textAnchor="middle"
        fill="white" fontSize="12" opacity="0.7"
        initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 2.2 }}
      >
        Compte ces 2 secondes au passage d&apos;un repère fixe
      </motion.text>
    </svg>
  );
}

function SchemaRondPoint({ accent }) {
  const acc = accent || '#cf2031';
  return (
    <svg viewBox="0 0 480 320" className="w-full max-w-2xl mx-auto">
      {/* Anneau */}
      <circle cx="240" cy="160" r="100" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="60" />
      <circle cx="240" cy="160" r="40" fill="rgba(255,255,255,0.05)" />
      <text x="240" y="165" textAnchor="middle" fill="white" fontSize="14" fontWeight="900" opacity="0.5">ÎLOT</text>

      {/* Entrée (bas) */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <rect x="220" y="270" width="40" height="50" fill="rgba(255,255,255,0.1)" />
        <text x="240" y="310" textAnchor="middle" fill="white" fontSize="11" fontWeight="900">ENTRÉE</text>
      </motion.g>

      {/* Sortie 1 (droite) - non prise */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
        <rect x="370" y="140" width="50" height="40" fill="rgba(255,255,255,0.1)" />
        <text x="395" y="195" textAnchor="middle" fill="white" fontSize="10" opacity="0.5">Sortie 1</text>
      </motion.g>

      {/* Sortie 2 (haut) - prise */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
        <rect x="220" y="0" width="40" height="50" fill={`${acc}33`} stroke={acc} strokeWidth="2" />
        <text x="240" y="65" textAnchor="middle" fill={acc} fontSize="11" fontWeight="900">SORTIE ✓</text>
      </motion.g>

      {/* Trajectoire moto : entrée → tour → sortie 2 */}
      <motion.path
        d="M 240 270 L 240 230 A 70 70 0 1 1 240 90 L 240 50"
        fill="none"
        stroke={acc}
        strokeWidth="4"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2.5, delay: 1.0, ease: 'easeInOut' }}
        markerEnd="url(#arrRp)"
      />
      <defs>
        <marker id="arrRp" markerWidth="10" markerHeight="10" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6 Z" fill={acc} />
        </marker>
      </defs>

      {/* Indicateur clignotant */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.0 }}>
        <motion.circle
          cx="310" cy="100" r="10" fill="#f59e0b"
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 0.6, repeat: Infinity }}
        />
        <text x="325" y="105" fill="white" fontSize="12" fontWeight="900">CLIGNOTANT DROIT</text>
        <text x="325" y="120" fill="white" fontSize="10" opacity="0.7">après la sortie 1</text>
      </motion.g>
    </svg>
  );
}

/* ─── Schémas SVG additionnels — Phase 16 ──────────────────────── */

function SchemaTrajectoireVirage({ accent }) {
  const acc = accent || '#cf2031';
  return (
    <svg viewBox="0 0 640 320" className="w-full max-w-3xl mx-auto">
      {/* Route en virage gauche (typique) */}
      <path d="M 20 240 Q 220 240 320 160 Q 420 80 620 80" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="80" strokeLinecap="round" />
      <path d="M 20 240 Q 220 240 320 160 Q 420 80 620 80" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeDasharray="10 6" />

      {/* Trajectoire IDÉALE ext → int → ext (vert) */}
      <motion.path
        d="M 30 270 Q 200 265 320 180 Q 440 95 615 50"
        fill="none" stroke="#16a34a" strokeWidth="5" strokeLinecap="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
        transition={{ duration: 2, delay: 0.3, ease: 'easeInOut' }}
        markerEnd="url(#arrTv)"
      />
      <defs>
        <marker id="arrTv" markerWidth="10" markerHeight="10" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6 Z" fill="#16a34a" />
        </marker>
      </defs>

      {/* Trajectoire À ÉVITER (corde directe rouge pointillé) */}
      <motion.path
        d="M 30 240 L 615 80"
        fill="none" stroke="#dc2626" strokeWidth="3" strokeDasharray="8 6"
        initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 2.3 }}
      />

      {/* Labels */}
      <motion.text x="60" y="295" fill="#16a34a" fontSize="14" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
        EXTÉRIEUR
      </motion.text>
      <motion.text x="280" y="155" fill="#16a34a" fontSize="14" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>
        CORDE
      </motion.text>
      <motion.text x="520" y="30" fill="#16a34a" fontSize="14" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}>
        EXTÉRIEUR
      </motion.text>
      <motion.text x="320" y="170" fill="#dc2626" fontSize="11" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.8 }}>
        ✗ Coupe d&apos;angle = danger
      </motion.text>

      <motion.text
        x="320" y="310" textAnchor="middle"
        fill="white" fontSize="13" fontWeight="700" opacity="0.7"
        initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 3.2 }}
      >
        Tu vois plus loin · ton angle est moindre · tu sors fort
      </motion.text>
    </svg>
  );
}

function SchemaAngleMortPL() {
  return (
    <svg viewBox="0 0 640 320" className="w-full max-w-3xl mx-auto">
      {/* Camion vu de dessus */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        {/* Remorque */}
        <rect x="180" y="100" width="220" height="120" rx="6" fill="#475569" stroke="white" strokeWidth="2" />
        {/* Cabine */}
        <rect x="400" y="115" width="80" height="90" rx="6" fill="#1e293b" stroke="white" strokeWidth="2" />
        {/* Vitres */}
        <rect x="410" y="125" width="60" height="25" rx="3" fill="#0891b2" opacity="0.6" />
        <text x="290" y="170" textAnchor="middle" fill="white" fontSize="16" fontWeight="900">CAMION</text>
      </motion.g>

      {/* Zones d'angle mort (rouges) */}
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 0.8 }}>
        {/* Avant droit */}
        <path d="M 480 130 L 600 90 L 600 160 L 480 160 Z" fill="#dc2626" opacity="0.45" />
        {/* Côté droit le long */}
        <path d="M 180 220 L 400 220 L 400 280 L 180 280 Z" fill="#dc2626" opacity="0.45" />
        {/* Arrière */}
        <path d="M 50 130 L 180 130 L 180 220 L 50 220 Z" fill="#dc2626" opacity="0.45" />
      </motion.g>

      {/* Labels danger */}
      <motion.text x="540" y="125" textAnchor="middle" fill="#dc2626" fontSize="11" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
        ANGLE MORT
      </motion.text>
      <motion.text x="290" y="265" textAnchor="middle" fill="#dc2626" fontSize="11" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}>
        ZONE AVEUGLE — Le + dangereux
      </motion.text>
      <motion.text x="115" y="180" textAnchor="middle" fill="#dc2626" fontSize="11" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}>
        ARRIÈRE
      </motion.text>

      {/* Moto en position sûre */}
      <motion.g initial={{ x: -50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 2.0 }}>
        <rect x="510" y="50" width="30" height="22" rx="4" fill="#16a34a" />
        <text x="525" y="40" textAnchor="middle" fill="#16a34a" fontSize="11" fontWeight="900">TOI</text>
        <text x="525" y="92" textAnchor="middle" fill="#16a34a" fontSize="10" fontWeight="700">visible</text>
      </motion.g>

      <motion.text
        x="320" y="305" textAnchor="middle"
        fill="white" fontSize="13" fontWeight="700" opacity="0.7"
        initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 2.5 }}
      >
        Si tu ne vois pas ses rétros, il ne te voit PAS.
      </motion.text>
    </svg>
  );
}

function SchemaInsertionVoieRapide({ accent }) {
  const acc = accent || '#cf2031';
  return (
    <svg viewBox="0 0 640 320" className="w-full max-w-3xl mx-auto">
      {/* Voie principale */}
      <rect x="0" y="80" width="640" height="60" fill="rgba(255,255,255,0.08)" />
      <line x1="0" y1="110" x2="640" y2="110" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeDasharray="20 12" />

      {/* Bande d'insertion (séparation) */}
      <path d="M 0 220 L 200 220 L 480 140 L 640 140" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
      <line x1="200" y1="220" x2="480" y2="140" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeDasharray="14 8" />

      {/* Voitures voie principale (déjà à 130) */}
      <motion.g initial={{ x: -50 }} animate={{ x: 0 }} transition={{ duration: 1 }}>
        <rect x="80" y="92" width="46" height="22" rx="4" fill="#9ca3af" />
        <text x="103" y="80" textAnchor="middle" fill="white" fontSize="10" fontWeight="900">130</text>
        <rect x="280" y="92" width="46" height="22" rx="4" fill="#9ca3af" />
        <text x="303" y="80" textAnchor="middle" fill="white" fontSize="10" fontWeight="900">130</text>
      </motion.g>

      {/* Toi : moto sur bande d'insertion, accélère */}
      <motion.g initial={{ x: 0 }} animate={{ x: 360 }} transition={{ duration: 2.5, delay: 0.5, ease: 'easeInOut' }}>
        <rect x="40" y="208" width="32" height="20" rx="3" fill={acc} />
        <text x="56" y="200" textAnchor="middle" fill={acc} fontSize="10" fontWeight="900">TOI</text>
      </motion.g>

      {/* Indicateur vitesse */}
      <motion.text
        x="60" y="262" fill="white" fontSize="14" fontWeight="900" fontFamily="Source Code Pro"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
      >50 km/h</motion.text>
      <motion.text
        x="270" y="240" fill="#f59e0b" fontSize="14" fontWeight="900" fontFamily="Source Code Pro"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
      >90 km/h</motion.text>
      <motion.text
        x="450" y="170" fill="#16a34a" fontSize="14" fontWeight="900" fontFamily="Source Code Pro"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.8 }}
      >130 km/h ✓</motion.text>

      {/* Labels */}
      <motion.text x="100" y="290" fill="white" fontSize="11" fontWeight="700" opacity="0.6" initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} transition={{ delay: 0.5 }}>
        Entrée bande
      </motion.text>
      <motion.text x="520" y="200" fill="white" fontSize="11" fontWeight="700" opacity="0.6" initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} transition={{ delay: 2.5 }}>
        Insertion
      </motion.text>

      <motion.text
        x="320" y="310" textAnchor="middle"
        fill="white" fontSize="13" fontWeight="700" opacity="0.7"
        initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 3.2 }}
      >
        Tu utilises TOUTE la bande pour atteindre 130 AVANT de t&apos;insérer.
      </motion.text>
    </svg>
  );
}

function SchemaPluieAdaptation() {
  return (
    <svg viewBox="0 0 600 320" className="w-full max-w-3xl mx-auto">
      {/* Gouttes de pluie animées */}
      {[...Array(15)].map((_, i) => (
        <motion.line
          key={i}
          x1={40 + i * 35} y1={0} x2={30 + i * 35} y2={50}
          stroke="#3b82f6" strokeWidth="2" opacity="0.6"
          animate={{ y1: [0, 320], y2: [50, 370] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.08, ease: 'linear' }}
        />
      ))}

      {/* Bloc Vitesse */}
      <motion.g initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3, type: 'spring' }}>
        <rect x="40" y="80" width="240" height="180" rx="14" fill="rgba(0,0,0,0.5)" stroke="#3b82f6" strokeWidth="3" />
        <text x="160" y="120" textAnchor="middle" fill="white" fontSize="14" fontWeight="900" letterSpacing="2">VITESSE</text>
        <text x="160" y="190" textAnchor="middle" fill="#3b82f6" fontSize="56" fontWeight="900" fontFamily="Source Code Pro">−20%</text>
        <text x="160" y="230" textAnchor="middle" fill="white" fontSize="11" opacity="0.8">Tu réduis ton allure</text>
      </motion.g>

      {/* Bloc Distance */}
      <motion.g initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.8, type: 'spring' }}>
        <rect x="320" y="80" width="240" height="180" rx="14" fill="rgba(0,0,0,0.5)" stroke="#dc2626" strokeWidth="3" />
        <text x="440" y="120" textAnchor="middle" fill="white" fontSize="14" fontWeight="900" letterSpacing="2">DISTANCE</text>
        <text x="440" y="190" textAnchor="middle" fill="#dc2626" fontSize="56" fontWeight="900" fontFamily="Source Code Pro">× 2</text>
        <text x="440" y="225" textAnchor="middle" fill="white" fontSize="11" opacity="0.8">Règle des 4 secondes</text>
        <text x="440" y="242" textAnchor="middle" fill="white" fontSize="11" opacity="0.8">(au lieu de 2)</text>
      </motion.g>

      <motion.text
        x="300" y="295" textAnchor="middle"
        fill="white" fontSize="14" fontWeight="900"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
      >
        Pluie = adhérence ÷ 2 → tu compenses
      </motion.text>
    </svg>
  );
}

function SchemaTunnelTransition() {
  return (
    <svg viewBox="0 0 640 320" className="w-full max-w-3xl mx-auto">
      <defs>
        <linearGradient id="tunnelGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="30%" stopColor="#fbbf24" />
          <stop offset="50%" stopColor="#1f2937" />
          <stop offset="100%" stopColor="#0c0a09" />
        </linearGradient>
      </defs>

      {/* Ciel/tunnel gradient */}
      <rect x="0" y="0" width="640" height="220" fill="url(#tunnelGrad)" />

      {/* Tunnel arch */}
      <motion.path
        d="M 280 220 L 280 80 Q 360 30 440 80 L 440 220 Z"
        fill="#0c0a09" stroke="#475569" strokeWidth="3"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
      />
      <motion.text x="360" y="80" textAnchor="middle" fill="white" fontSize="14" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
        TUNNEL
      </motion.text>

      {/* Route */}
      <rect x="0" y="220" width="640" height="100" fill="#1c1917" />
      <line x1="0" y1="270" x2="640" y2="270" stroke="white" strokeWidth="3" strokeDasharray="20 14" opacity="0.4" />

      {/* Moto avec feux animés */}
      <motion.g initial={{ x: 0 }} animate={{ x: 280 }} transition={{ duration: 3, delay: 0.8, ease: 'linear' }}>
        <rect x="50" y="240" width="36" height="22" rx="4" fill="#cf2031" />
        <text x="68" y="232" textAnchor="middle" fill="#cf2031" fontSize="10" fontWeight="900">TOI</text>
        {/* Faisceau de phare */}
        <motion.path
          d="M 86 250 L 130 240 L 130 260 Z"
          fill="#fbbf24"
          animate={{ opacity: [0, 1, 1] }}
          transition={{ duration: 3, times: [0, 0.3, 1], delay: 0.8 }}
        />
      </motion.g>

      {/* Labels étapes */}
      <motion.text x="100" y="50" fill="#fbbf24" fontSize="13" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
        ☀ Visière relevée
      </motion.text>
      <motion.text x="500" y="50" fill="white" fontSize="13" fontWeight="900" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}>
        💡 Feux croisement
      </motion.text>

      <motion.text
        x="320" y="305" textAnchor="middle"
        fill="white" fontSize="13" fontWeight="700" opacity="0.7"
        initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 2.5 }}
      >
        Entrée tunnel : tes yeux mettent 3 sec à s&apos;adapter. Prépare-toi.
      </motion.text>
    </svg>
  );
}

const SCHEMA_VARIANTS = {
  freinage7030: SchemaFreinage7030,
  slalomS: SchemaSlalomS,
  regle2sec: SchemaRegle2Sec,
  rondPoint: SchemaRondPoint,
  trajectoireVirage: SchemaTrajectoireVirage,
  angleMortPL: SchemaAngleMortPL,
  insertionVoieRapide: SchemaInsertionVoieRapide,
  pluieAdaptation: SchemaPluieAdaptation,
  tunnelTransition: SchemaTunnelTransition,
};

function SchemaSvgSlide({ slide }) {
  const Variant = SCHEMA_VARIANTS[slide.variant];
  return (
    <Shell accent={slide.accent || '#0891b2'} badge={slide.title || '📐 Schéma'} icon={Eye}>
      <div className="max-w-5xl mx-auto w-full">
        {slide.subtitle && (
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center text-xl md:text-2xl font-black text-white/80 mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {slide.subtitle}
          </motion.p>
        )}
        {Variant ? <Variant accent={slide.accent} /> : (
          <div className="text-white/40 italic text-center">Schéma SVG « {slide.variant} » non implémenté.</div>
        )}
      </div>
    </Shell>
  );
}

/* ─── Map renderers → kinds ──────────────────────────────────────── */
const RENDERERS = {
  title: TitleSlide,
  phraseCle: PhraseCleSlide,
  phase: PhaseSlide,
  end: EndSlide,
  qcm: QcmSlide,
  video: VideoSlide,
  stat: StatSlide,
  phraseChoc: PhraseChocSlide,
  schemaSvg: SchemaSvgSlide,
};

/* ─── Composant principal ────────────────────────────────────────── */
export default function CoursCanevasPresentation({ kind = 'plateau' }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const fiche = useMemo(() => {
    if (kind === 'plateau') return getFichePlateauBySlug(slug);
    return getFicheCirculationBySlug(slug);
  }, [kind, slug]);
  const interactif = useMemo(() => getInteractifBySlug(slug), [slug]);
  const slides = useMemo(() => (fiche ? buildSlides(fiche, kind, interactif) : []), [fiche, kind, interactif]);
  const [idx, setIdx] = useState(0);
  const sounds = useSoundEffects();

  const handleQcmReveal = useCallback((correct) => {
    if (correct) sounds.playSuccess?.();
    else sounds.playError?.();
  }, [sounds]);

  useSeo({
    title: fiche
      ? `Présentation Pro · ${fiche.title || fiche.titre} — Espace enseignant`
      : 'Présentation cours',
    description: 'Mode présentation studio TV slide-par-slide avec animations, navigation clavier, mode plein écran.',
    path: `/enseignant/${kind === 'plateau' ? 'terrain' : 'seance-circulation'}/${slug}/presenter`,
  });

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, slides.length - 1)), [slides.length]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);
  const backUrl = kind === 'plateau' ? `/enseignant/terrain/${slug}` : `/enseignant/seance-circulation/${slug}`;

  useEffect(() => {
    const onKey = (e) => {
      if (['ArrowRight', ' ', 'PageDown'].includes(e.key)) { e.preventDefault(); next(); }
      else if (['ArrowLeft', 'PageUp'].includes(e.key)) { e.preventDefault(); prev(); }
      else if (e.key === 'Escape') navigate(backUrl);
      else if (e.key === 'Home') setIdx(0);
      else if (e.key === 'End') setIdx(slides.length - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, navigate, backUrl, slides.length]);

  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    return () => { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); };
  }, []);

  if (!fiche) {
    return (
      <div className="min-h-screen bg-black text-white p-8 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-black mb-4">Fiche introuvable</h1>
          <Link to={kind === 'plateau' ? '/enseignant/terrain' : '/enseignant/seance-circulation'}
            className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20">Retour</Link>
        </div>
      </div>
    );
  }

  const slide = slides[idx];
  const Renderer = RENDERERS[slide.kind] || TitleSlide;
  const progress = ((idx + 1) / slides.length) * 100;

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden c2r-presentation-mode"
      style={{ background: 'radial-gradient(ellipse at center, #1a1a1a 0%, #050505 100%)', color: 'white' }}
      data-testid="canevas-presentation">
      <div className="absolute top-0 left-0 right-0 z-10 px-6 py-3 flex items-center gap-3 bg-black/50 backdrop-blur border-b border-white/10">
        <Link to={backUrl}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-wider"
          data-testid="btn-exit-presentation">
          <X className="w-3.5 h-3.5" /> Quitter
        </Link>
        <span className="text-xs font-black uppercase tracking-widest text-white/50">{fiche.title || fiche.titre}</span>
        {interactif && (
          <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest"
            style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white' }}
            data-testid="badge-premium">
            <Sparkles className="w-3 h-3" /> Premium
          </span>
        )}
        <div className="flex-1" />
        <button type="button" onClick={sounds.toggleMute}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-wider"
          data-testid="btn-mute">
          {sounds.muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          {sounds.muted ? 'Muet' : 'Sons'}
        </button>
        <span className="text-xs font-black tabular-nums text-white/70">{idx + 1} / {slides.length}</span>
      </div>

      <div className="absolute top-12 left-0 right-0 z-10 h-0.5 bg-white/10">
        <motion.div className="h-full" style={{ background: slide.accent }}
          animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
      </div>

      <div className="absolute inset-0 pt-20 pb-20 px-8 lg:px-16 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <div key={idx} className="w-full max-w-7xl">
            <Renderer slide={slide} onReveal={handleQcmReveal} />
          </div>
        </AnimatePresence>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10 px-6 py-4 flex items-center justify-between gap-3 bg-black/50 backdrop-blur border-t border-white/10">
        <button type="button" onClick={prev} disabled={idx === 0}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 text-sm font-black uppercase tracking-wider"
          data-testid="btn-prev">
          <ChevronLeft className="w-4 h-4" /> Précédent
        </button>
        <span className="text-[10px] text-white/40 uppercase tracking-widest hidden md:inline">
          ← → flèches · Espace · Échap pour quitter
        </span>
        <button type="button" onClick={next} disabled={idx === slides.length - 1}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 text-sm font-black uppercase tracking-wider"
          data-testid="btn-next">
          Suivant <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
